import { useState } from 'react';
import { Settings, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { MissionControlConfig } from '../../types';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

interface Props {
  config: MissionControlConfig | null;
  orgId: string;
  userId: string;
  onSaved: (config: MissionControlConfig) => void;
}

export default function ConfigPanel({ config, orgId, onSaved }: Props) {
  const { apiPut } = useApi();
  const [open, setOpen] = useState(!config);
  const [saving, setSaving] = useState(false);
  const [dueDay, setDueDay] = useState(config?.objectives_due_day || 'monday');
  const [reviewFreq, setReviewFreq] = useState(config?.review_frequency_weeks || 1);
  const [reviewDay, setReviewDay] = useState(config?.review_day || 'friday');
  const [minAccounts, setMinAccounts] = useState(config?.min_accounts ?? 5);
  const [minContacts, setMinContacts] = useState(config?.min_contacts_per_account ?? 5);

  async function handleSave() {
    setSaving(true);
    try {
      // leader_user_id is derived server-side from the authenticated caller.
      const { config: saved } = await apiPut<{ config: MissionControlConfig }>(
        '/v1/mission-control/config',
        {
          org_id: orgId,
          objectives_due_day: dueDay,
          review_frequency_weeks: reviewFreq,
          review_day: reviewDay,
          min_accounts: minAccounts,
          min_contacts_per_account: minContacts,
        },
      );
      if (saved) onSaved(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-app-canvas/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-forest-50 flex items-center justify-center">
            <Settings className="w-4 h-4 text-forest-600" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-app-ink">Objective Cadence Settings</h3>
            <p className="text-xs text-app-muted mt-0.5">
              {config
                ? `Due ${config.objectives_due_day}s, reviewed ${config.review_day}s every ${config.review_frequency_weeks} week${config.review_frequency_weeks > 1 ? 's' : ''}`
                : 'Configure when your team defines and reviews objectives'}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-app-line pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Objectives due by</label>
              <select
                value={dueDay}
                onChange={e => setDueDay(e.target.value)}
                className="w-full rounded-lg border border-app-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500"
              >
                {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Review day</label>
              <select
                value={reviewDay}
                onChange={e => setReviewDay(e.target.value)}
                className="w-full rounded-lg border border-app-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500"
              >
                {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Review frequency</label>
              <select
                value={reviewFreq}
                onChange={e => setReviewFreq(Number(e.target.value))}
                className="w-full rounded-lg border border-app-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500"
              >
                {[1, 2, 3, 4].map(n => <option key={n} value={n}>Every {n} week{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Suggested accounts</label>
              <input
                type="number"
                min={1}
                max={50}
                value={minAccounts}
                onChange={e => setMinAccounts(Number(e.target.value))}
                className="w-full rounded-lg border border-app-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Suggested contacts per account</label>
              <input
                type="number"
                min={1}
                max={50}
                value={minContacts}
                onChange={e => setMinContacts(Number(e.target.value))}
                className="w-full rounded-lg border border-app-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
